import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createProject } from '@/services/api';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft } from 'lucide-react';
import { industries } from './projectConstants';

export const CreateProjectPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    industry: 'logistics',
    problem_statement: '',
    goal: '',
    scope: '',
    team_members: [],
    target_completion: ''
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const project = await createProject(formData);
      toast.success('Project created successfully!');
      navigate(`/projects/${project.project_id}`);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto space-y-6" data-testid="create-project-page">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/projects')} data-testid="back-btn">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">New DMAIC Project</h1>
            <p className="text-muted-foreground font-body">Define your improvement initiative</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Project Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="title">Project Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  placeholder="e.g., Reduce Delivery Time Variance"
                  required
                  data-testid="project-title"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  placeholder="Brief overview of the project..."
                  rows={3}
                  required
                  data-testid="project-description"
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="industry">Industry</Label>
                  <Select value={formData.industry} onValueChange={(v) => handleChange('industry', v)}>
                    <SelectTrigger data-testid="project-industry">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {industries.map((ind) => (
                        <SelectItem key={ind.value} value={ind.value}>{ind.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="target_completion">Target Completion</Label>
                  <Input
                    id="target_completion"
                    type="date"
                    value={formData.target_completion}
                    onChange={(e) => handleChange('target_completion', e.target.value)}
                    data-testid="project-target-date"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Define Phase (Charter)</CardTitle>
              <CardDescription>Document the problem statement, goals, and scope</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="problem_statement">Problem Statement *</Label>
                <Textarea
                  id="problem_statement"
                  value={formData.problem_statement}
                  onChange={(e) => handleChange('problem_statement', e.target.value)}
                  placeholder="Clearly describe the problem you're trying to solve..."
                  rows={3}
                  required
                  data-testid="project-problem"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="goal">Project Goal *</Label>
                <Textarea
                  id="goal"
                  value={formData.goal}
                  onChange={(e) => handleChange('goal', e.target.value)}
                  placeholder="What specific, measurable outcome are you targeting?"
                  rows={2}
                  required
                  data-testid="project-goal"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="scope">Project Scope *</Label>
                <Textarea
                  id="scope"
                  value={formData.scope}
                  onChange={(e) => handleChange('scope', e.target.value)}
                  placeholder="Define what's included and excluded from this project..."
                  rows={2}
                  required
                  data-testid="project-scope"
                />
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end gap-4">
            <Button type="button" variant="outline" onClick={() => navigate('/projects')}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading} data-testid="submit-project">
              {loading ? 'Creating...' : 'Create Project'}
            </Button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
};

export default CreateProjectPage;
